import React from 'react'
import NavbarComponent from '../components/navbar'
import { Form, Button } from 'react-bootstrap'
import UserProfile from '../user'
import { useNavigate, Link } from 'react-router-dom'
import { getUser, addUser } from '../api'

import Footer from '../components/footer'
import './styles/landingpage.css'

const Meter = ({ name, score, confidence }) => (
    <div className="ocn-meter">
        <div className="row"><b>{name}</b><span>score {score} · confidence {confidence}</span></div>
        <div className="ocn-bar" aria-hidden="true">
            {Array.from({ length: 10 }, (_, i) => <i key={i} className={i < score ? 'on' : ''} />)}
        </div>
    </div>
)

const LandingPage = () => {
    const navigate = useNavigate()

    const loginUser = async (e) => {
        e.preventDefault()
        const github = e.target[0].value.trim().replace(/\/+$/, '').split('/').pop()
        const prog_lang = e.target[1].value.trim()
        if (!github || !prog_lang) {
            window.alert("Please enter your GitHub profile link and primary programming language.")
            return
        }
        try {
            const existing = await getUser(github)
            if (existing.length > 0) {
                window.alert("Welcome back. You are already signed up, so you can start labelling right away.")
            } else {
                await addUser(github, prog_lang)
                window.alert("You are signed up. You can now start labelling commits.")
            }
        } catch (error) {
            console.log(error)
            window.alert(`Could not sign you up: ${error.message}`)
            return
        }
        UserProfile.setName(github)
        navigate('/label')
    }

    return (
        <>
            <NavbarComponent />
            <main>
                <section className="ocn-wrap ocn-hero">
                    <div>
                        <p className="ocn-eyebrow">Open research data</p>
                        <h1>Human judgements of software code, labelled by developers</h1>
                        <p className="lede">
                            OpenCodeNet is a crowdsourced, open database of labelled code commits from public GitHub
                            repositories. Developers rate commits on attributes such as novelty and usefulness, and
                            researchers use the labels to train and benchmark models that evaluate software code.
                        </p>
                        <div className="ocn-stats">
                            <div><b>3,000+</b><span>labelled code snippets</span></div>
                            <div><b>6</b><span>programming languages</span></div>
                            <div><b>1–10</b><span>score and confidence per label</span></div>
                        </div>
                    </div>

                    <div className="ocn-card">
                        <h2>Start labelling</h2>
                        <p className="ocn-hint">Sign up with your GitHub profile. If you have signed up before, enter the same profile to continue.</p>
                        <Form onSubmit={loginUser}>
                            <Form.Group className="mb-3" controlId="github_profile_link">
                                <Form.Label>GitHub profile link</Form.Label>
                                <Form.Control type="text" placeholder="https://github.com/username" required />
                            </Form.Group>
                            <Form.Group className="mb-4" controlId="language">
                                <Form.Label>Primary programming language</Form.Label>
                                <Form.Control type="text" placeholder="e.g. Python" required />
                            </Form.Group>
                            <Button variant="primary" type="submit" className="w-100">Continue</Button>
                        </Form>
                    </div>
                </section>

                <section className="ocn-wrap">
                    <h2 className="ocn-section-title">How it works</h2>
                    <div className="ocn-steps">
                        <div className="ocn-card">
                            <span className="ocn-step">1</span>
                            <h3>Get a commit</h3>
                            <p>Choose a language and OpenCodeNet picks a random commit from a popular public GitHub repository.</p>
                        </div>
                        <div className="ocn-card">
                            <span className="ocn-step">2</span>
                            <h3>Label and score it</h3>
                            <p>Open the commit, choose the attributes you want to rate, and give each a score and your confidence from 1 to 10.</p>
                        </div>
                        <div className="ocn-card">
                            <span className="ocn-step">3</span>
                            <h3>Download the data</h3>
                            <p>Search the dataset by label and download the labelled commits as a CSV for your own research.</p>
                        </div>
                    </div>
                </section>

                <section className="ocn-wrap">
                    <h2 className="ocn-section-title">What a label looks like</h2>
                    <figure className="ocn-figure">
                        <div className="ocn-card ocn-example">
                            <div className="ocn-diff" aria-label="Example commit diff">
<span className="meta">{"commit 9f3c2e1  ·  Feature  ·  src/cache.py"}</span>{"\n"}
<span className="del">{"- def get(self, key):"}</span>
<span className="del">{"-     return self.store.get(key)"}</span>
<span className="add">{"+ def get(self, key, default=None):"}</span>
<span className="add">{"+     entry = self.store.get(key)"}</span>
<span className="add">{"+     if entry and entry.expires < time.monotonic():"}</span>
<span className="add">{"+         self.store.pop(key, None)"}</span>
<span className="add">{"+         return default"}</span>
<span className="add">{"+     return entry.value if entry else default"}</span>
                            </div>
                            <div className="ocn-example-side">
                                <p className="ocn-eyebrow" style={{ margin: 0 }}>Labels from one contributor</p>
                                <Meter name="novelty" score={6} confidence={8} />
                                <Meter name="usefulness" score={8} confidence={9} />
                                <Meter name="complexity" score={4} confidence={7} />
                                <span className="ocn-muted" style={{ fontSize: '0.85rem' }}>Type of commit: <span className="ocn-tag">Feature</span></span>
                            </div>
                        </div>
                        <figcaption>Illustrative example. Each label records the commit, its type, a 1–10 score and the rater’s confidence.</figcaption>
                    </figure>
                </section>

                <section className="ocn-wrap">
                    <h2 className="ocn-section-title">Research using the labels</h2>
                    <div className="ocn-steps" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                        <div className="ocn-card">
                            <span className="ocn-tag">Research Policy · 2025</span>
                            <h3 style={{ marginTop: '0.6rem' }}>Write access, ownership and project novelty</h3>
                            <p>Expert novelty labels scale to a million commits and show that wider write access raises novelty but lowers project survival.</p>
                        </div>
                        <div className="ocn-card">
                            <span className="ocn-tag">Working paper</span>
                            <h3 style={{ marginTop: '0.6rem' }}>Measuring implementation novelty</h3>
                            <p>Code vectors from syntax trees predict expert novelty ratings better than text, and link novelty to code reuse and pull-request acceptance.</p>
                        </div>
                    </div>
                    <Link to="/projects" className="btn btn-outline-secondary">See the projects →</Link>
                </section>
            </main>
            <Footer />
        </>
    )
}

export default LandingPage
